import { ReactNode, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCep } from "@/lib/formatters";
import { FieldError } from "@/lib/customerRegistrationError";
import {
  ADDRESS_TYPE_LABELS,
  addressFormSchema,
  AddressFormValues,
  BRAZILIAN_STATES,
} from "@/lib/validators/address";

interface AddressFormProps {
  defaultValues: AddressFormValues;
  /** Edição: a finalidade não muda depois de criado e o padrão é trocado pela lista. */
  isEditing?: boolean;
  isSubmitting?: boolean;
  /** Erros de campo devolvidos pela API (400), exibidos junto do campo. */
  serverErrors?: FieldError[];
  onSubmit: (values: AddressFormValues) => void;
  onCancel: () => void;
  /** Conteúdo extra antes dos botões, conforme a finalidade escolhida (ex.: "usar o mesmo para entrega"). */
  renderBeforeActions?: (addressType: AddressFormValues["addressType"]) => ReactNode;
}

/** Formulário de endereço do cliente CNPJ e do cliente pessoa física. */
export function AddressForm({
  defaultValues,
  isEditing = false,
  isSubmitting = false,
  serverErrors = [],
  onSubmit,
  onCancel,
  renderBeforeActions,
}: AddressFormProps) {
  const form = useForm<AddressFormValues>({
    resolver: zodResolver(addressFormSchema),
    defaultValues,
  });
  const addressType = useWatch({ control: form.control, name: "addressType" });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  useEffect(() => {
    for (const error of serverErrors) {
      if (error.field in defaultValues) {
        form.setError(error.field as keyof AddressFormValues, { message: error.message });
      }
    }
  }, [serverErrors, defaultValues, form]);

  const text = (name: keyof Omit<AddressFormValues, "isDefault" | "addressType" | "state">, label: string, props?: { placeholder?: string; inputMode?: "numeric" }) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input {...field} {...props} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="addressType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Finalidade</FormLabel>
                <Select value={field.value} onValueChange={field.onChange} disabled={isEditing}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Billing">{ADDRESS_TYPE_LABELS.Billing}</SelectItem>
                    <SelectItem value="Shipping">{ADDRESS_TYPE_LABELS.Shipping}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="postcode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>CEP</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    inputMode="numeric"
                    placeholder="00000-000"
                    onBlur={() => {
                      field.onChange(formatCep(field.value));
                      field.onBlur();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {text("street", "Logradouro")}

        <div className="grid gap-4 sm:grid-cols-3">
          {text("number", "Número")}
          <div className="sm:col-span-2">{text("complement", "Complemento")}</div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          {text("district", "Bairro")}
          {text("city", "Cidade")}
          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem>
                <FormLabel>UF</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="UF" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {BRAZILIAN_STATES.map((state) => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {text("label", "Apelido (opcional)", { placeholder: "Ex.: Unidade Centro" })}

        {!isEditing && (
          <FormField
            control={form.control}
            name="isDefault"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center gap-2 space-y-0">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={(checked) => field.onChange(checked === true)} />
                </FormControl>
                <FormLabel className="font-normal">Tornar padrão desta finalidade</FormLabel>
              </FormItem>
            )}
          />
        )}

        {renderBeforeActions?.(addressType)}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Salvando..." : "Salvar endereço"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
